import { createDescribeYourSolutionFields, createFillInTheBasicsFields, createGrowYourBusinessFields, createIncludeVisualsFields, createJapaneseCompanyDetails, createLinkYourSolutionFields, createRegisterYourPackageFields, createSetPricingApprovalsFields, createSetPricingFields, getMessages, getStepErrorMap, selectExtensionListing, selectSolutionExtension } from './listingValidatorUtilities';
import { logger } from './logger';
import { isConsultantType } from './schemaUtilities';
import { checkStepCompleted, hasErrorInStep } from './validatorUtilities';
export class ListingValidator {
  constructor(listing, options) {
    this.isPricingValid = void 0;
    this.isSolutionValid = void 0;
    this.isApproved = void 0;
    this.isPackageRegistered = void 0;
    this._listing = void 0;
    this._listingExtension = void 0;
    this._stepsCompleted = void 0;
    this._solutionExtension = void 0;
    this.listing = listing;
    this.isPricingValid = options === null || options === void 0 ? void 0 : options.isPricingValid;
    this.isSolutionValid = options === null || options === void 0 ? void 0 : options.isSolutionValid;
    this.isApproved = options === null || options === void 0 ? void 0 : options.isApproved;
    this.isPackageRegistered = options === null || options === void 0 ? void 0 : options.isPackageRegistered;
  }
  set listing(listing) {
    if (listing !== this._listing) {
      this._listing = listing;
      this._listingExtension = selectExtensionListing(this._listing.technology, listing);
      if (this._listingExtension && Object.prototype.hasOwnProperty.call(this._listingExtension, 'extensions')) {
        this._solutionExtension = selectSolutionExtension(this._listing.technology, this._listingExtension);
      }
    }
  }
  get listing() {
    return this._listing;
  }
  get listingExtension() {
    return this._listingExtension;
  }
  get numberOfSteps() {
    return isConsultantType(this._listing.technology) ? 3 : 5;
  }
  get stepsCompleted() {
    if (!this._listing || !this._listingExtension) {
      return [];
    }

    // Since computing the number of steps completed is an expensive operation,
    // cache them and invalidate if the listing is reassigned.
    if (this._stepsCompleted) {
      return this._stepsCompleted;
    }
    this._stepsCompleted = [];
    if (isConsultantType(this._listing.technology)) {
      for (let i = 1; i <= this.numberOfSteps; i++) {
        if (i === 2) {
          if (checkStepCompleted(this.getFieldsForStep('2-1')) && checkStepCompleted(this.getFieldsForStep('2-2'))) {
            this._stepsCompleted.push(i);
          }
        } else if (checkStepCompleted(this.getFieldsForStep(i.toString()))) {
          this._stepsCompleted.push(i);
        }
      }
    } else {
      for (let i = 1; i <= this.numberOfSteps; i++) {
        if (i === 2) {
          if (checkStepCompleted(this.getFieldsForStep('2-1')) && checkStepCompleted(this.getFieldsForStep('2-2'))) {
            this._stepsCompleted.push(i);
          }
        } else if (i === 3) {
          if (checkStepCompleted(this.getFieldsForStep('3-1')) && checkStepCompleted(this.getFieldsForStep('3-2'))) {
            this._stepsCompleted.push(i);
          }
        } else if (checkStepCompleted(this.getFieldsForStep(i.toString()))) {
          this._stepsCompleted.push(i);
        }
      }
    }
    return this._stepsCompleted;
  }
  get isValid() {
    return this.stepsCompleted.length === this.numberOfSteps;
  }
  resetCachedStepsCompleted() {
    this._stepsCompleted = undefined;
  }
  getFieldsForStep(numberOfStep) {
    if (isConsultantType(this._listing.technology)) {
      switch (numberOfStep) {
        case '1':
          return createFillInTheBasicsFields(this._listing, this._listingExtension);
        case '2-1':
          return createDescribeYourSolutionFields(this._listing, this._listingExtension);
        case '2-2':
          return createIncludeVisualsFields(this._listing);
        case '3':
          return createGrowYourBusinessFields(this._listing, this._listingExtension);
        default:
          return [];
      }
    } else {
      switch (numberOfStep) {
        case '1':
          return createFillInTheBasicsFields(this._listing, this._listingExtension);
        case '2-1':
          return createSetPricingFields(this._listing, this.isPricingValid);
        case '2-2':
          return createSetPricingApprovalsFields(this.isApproved);
        case '3-1':
          return createDescribeYourSolutionFields(this._listing, this._listingExtension);
        case '3-2':
          return createIncludeVisualsFields(this._listing);
        case '4':
          return createLinkYourSolutionFields(this._listing, this._listingExtension, this.isSolutionValid);
        case '5':
          return createGrowYourBusinessFields(this._listing, this._listingExtension);
        case '6':
          return createRegisterYourPackageFields(this.isPackageRegistered);
        default:
          return [];
      }
    }
  }
  getStepFields() {
    let result;
    if (isConsultantType(this._listing.technology)) {
      result = {
        '1': createFillInTheBasicsFields(this._listing, this._listingExtension),
        '2-1': createDescribeYourSolutionFields(this._listing, this._listingExtension),
        '2-2': createIncludeVisualsFields(this._listing),
        '3': createGrowYourBusinessFields(this._listing, this._listingExtension)
      };
    } else {
      result = {
        '1': createFillInTheBasicsFields(this._listing, this._listingExtension),
        '2-1': createSetPricingFields(this._listing, this.isPricingValid),
        '2-2': createSetPricingApprovalsFields(this.isApproved),
        '3-1': createDescribeYourSolutionFields(this._listing, this._listingExtension),
        '3-2': createIncludeVisualsFields(this._listing),
        '4': createLinkYourSolutionFields(this._listing, this._listingExtension, this.isSolutionValid),
        '5': createGrowYourBusinessFields(this._listing, this._listingExtension),
        '6': createRegisterYourPackageFields(this.isPackageRegistered)
      };
    }
    result.japaneseCompanyDetails = createJapaneseCompanyDetails(this._listingExtension);
    return result;
  }
  getStepsWithErrors(errors, returnOnFirstError = true) {
    const stepList = this.getStepFields();
    const steps = Object.keys(stepList);
    const stepsWithErrors = new Set();
    const schemaStepErrorMap = getStepErrorMap(this._listing.technology);

    // Here's an example of a possible error stack: instance.extensions[0].data.extensions[0].data.installUrl does not conform to the \"uri\" format

    const cleanedSupportInfoErrors = cleanSupportInfoItemsErrors(errors);
    for (const error of cleanedSupportInfoErrors) {
      var _ref, _matches, _this$_solutionExtens, _matches$2$match$grou, _matches$, _matches$$match, _matches$$match$group, _matches2, _this$_listingExtensi, _matches$1$match$grou, _matches$2, _matches$2$match, _matches$2$match$grou2, _matches3, _matches$0$match$grou, _matches$3, _matches$3$match, _matches$3$match$grou, _matches$groups$index, _matches4, _matches4$groups;
      //For errors like `extensions[0].data.highlights[0]` we need to remove the last item from the array `[0]` to process the field correctly
      if (Array.isArray(error.path) && !isNaN(parseInt(error.path[error.path.length - 1]))) {
        error.path.pop();
      }

      // Check for AgentForce field level errors
      if (Array.isArray(error.path) && (error.path.includes('numAgents') || error.path.includes('numTopics') || error.path.includes('numActions') || error.path.includes('description'))) {
        // For Copilot fields, always map to step 1
        if (this._listing.technology === 'force/copilot') {
          stepsWithErrors.add('1');
          if (returnOnFirstError) {
            return stepsWithErrors;
          }
          continue;
        }
      }
      const stack = Array.isArray(error.path) ? error.path[error.path.length - 1] : error.stack;
      const name = stack === 'data' ? error.argument || '' : stack;
      for (const step of steps) {
        if (stepsWithErrors.has(step)) {
          continue;
        }
        if (hasErrorInStep(stepList[step], name)) {
          stepsWithErrors.add(step);
          if (returnOnFirstError) {
            return stepsWithErrors;
          }
        }
      }

      // Look for extensions
      let matchType = error.stack;
      let matches = error.stack.match(/extensions*\[(?<index>[0-9]+)\]/g);
      const {
        extensions,
        extensionIndex
      } = (_ref = ((_matches = matches) === null || _matches === void 0 ? void 0 : _matches.length) === 3 ? {
        extensions: (_this$_solutionExtens = this._solutionExtension) === null || _this$_solutionExtens === void 0 ? void 0 : _this$_solutionExtens.extensions,
        extensionIndex: parseInt((_matches$2$match$grou = (_matches$ = matches[2]) === null || _matches$ === void 0 ? void 0 : (_matches$$match = _matches$.match(/extensions*\[(?<index>[0-9]+)\]/)) === null || _matches$$match === void 0 ? void 0 : (_matches$$match$group = _matches$$match.groups) === null || _matches$$match$group === void 0 ? void 0 : _matches$$match$group.index) !== null && _matches$2$match$grou !== void 0 ? _matches$2$match$grou : '-1')
      } : ((_matches2 = matches) === null || _matches2 === void 0 ? void 0 : _matches2.length) === 2 ? {
        extensions: (_this$_listingExtensi = this._listingExtension) === null || _this$_listingExtensi === void 0 ? void 0 : _this$_listingExtensi.extensions,
        extensionIndex: parseInt((_matches$1$match$grou = (_matches$2 = matches[1]) === null || _matches$2 === void 0 ? void 0 : (_matches$2$match = _matches$2.match(/extensions*\[(?<index>[0-9]+)\]/)) === null || _matches$2$match === void 0 ? void 0 : (_matches$2$match$grou2 = _matches$2$match.groups) === null || _matches$2$match$grou2 === void 0 ? void 0 : _matches$2$match$grou2.index) !== null && _matches$1$match$grou !== void 0 ? _matches$1$match$grou : '-1')
      } : ((_matches3 = matches) === null || _matches3 === void 0 ? void 0 : _matches3.length) === 1 ? {
        extensions: this._listing.extensions,
        extensionIndex: parseInt((_matches$0$match$grou = (_matches$3 = matches[0]) === null || _matches$3 === void 0 ? void 0 : (_matches$3$match = _matches$3.match(/extensions*\[(?<index>[0-9]+)\]/)) === null || _matches$3$match === void 0 ? void 0 : (_matches$3$match$grou = _matches$3$match.groups) === null || _matches$3$match$grou === void 0 ? void 0 : _matches$3$match$grou.index) !== null && _matches$0$match$grou !== void 0 ? _matches$0$match$grou : '-1')
      } : undefined) !== null && _ref !== void 0 ? _ref : {
        extensions: undefined,
        extensionIndex: -1
      };
      if (extensions && extensionIndex !== -1) {
        const extension = extensions === null || extensions === void 0 ? void 0 : extensions[extensionIndex];
        if (!extension || !extension.extensionType) {
          logger.error(`Failed to find listing extension with index=${extensionIndex}.`);
          continue;
        }
        matchType = extension.extensionType;
      }
      let step = schemaStepErrorMap[matchType];
      if (step && steps.indexOf(step) === -1) {
        logger.error(`Step ${step} that has an error was not included on the steps of the listing.`);
        continue;
      }
      if (step) {
        stepsWithErrors.add(step);
        if (returnOnFirstError) {
          return stepsWithErrors;
        }
        continue;
      }

      // Look for plugins

      matches = error.stack.match(/plugins*\[(?<index>[0-9]+)\]/);
      const pluginIndex = parseInt((_matches$groups$index = (_matches4 = matches) === null || _matches4 === void 0 ? void 0 : (_matches4$groups = _matches4.groups) === null || _matches4$groups === void 0 ? void 0 : _matches4$groups.index) !== null && _matches$groups$index !== void 0 ? _matches$groups$index : '-1');
      if (pluginIndex !== -1) {
        var _this$_listing$plugin;
        const plugin = (_this$_listing$plugin = this._listing.plugins) === null || _this$_listing$plugin === void 0 ? void 0 : _this$_listing$plugin[pluginIndex];
        if (!plugin || !plugin.pluginType) {
          logger.error(`Failed to find listing plugin with index=${pluginIndex}.`);
          continue;
        }
        matchType = plugin.pluginType;
      } else {
        matchType = error.stack;
      }
      if (error.stack.includes('supportEmailAddress')) {
        step = '3-1';
      } else {
        step = schemaStepErrorMap[matchType];
      }
      if (step && steps.indexOf(step) === -1) {
        logger.error(`Step ${step} that has an error was not included on the steps of the listing.`);
        continue;
      }
      if (step && !stepsWithErrors[step]) {
        stepsWithErrors.add(step);
        if (returnOnFirstError) {
          return stepsWithErrors;
        }
        continue;
      }
      logger.error(`Found a validation error ${matchType}, but was missing an entry for it in STEP_ERROR_MAP.`);
    }
    return stepsWithErrors;
  }
}

/**
 * Filters out 'requires property "items"' errors from the given list of errors in case of they are related to SupportInformation plugin validation.
 * This is done, because in case of fail on the validation -a wrong formated support email address-, oAF response include unrelated "requires property "items" errors,
 * even tho the SupportInformation dont have the "items" field in it schema, in oposition to the others plugins types.
 *
 * This is a workaround until we redesing extensions/plugins schemas.
 * @param errors - The list of errors to filter.
 *
 * @returns The filtered list of errors.
 */
export function cleanSupportInfoItemsErrors(errors) {
  const error = errors.filter(err => err.stack.includes('supportEmailAddress'));
  if (error.length === 1) {
    const path = error[0].stack.split('supportEmailAddress');
    const cleaned = errors.filter(err => {
      var _err$message;
      return !(!err.stack.includes(path[0]) && (_err$message = err.message) !== null && _err$message !== void 0 && _err$message.includes('requires property "items"'));
    });
    return cleaned;
  }
  return errors;
}
export function getMessage(listing) {
  const messages = getMessages(listing);
  if (messages.errors && messages.errors.length > 0) {
    var _messages$errors$0$me;
    return {
      type: 'error',
      text: (_messages$errors$0$me = messages.errors[0].message) !== null && _messages$errors$0$me !== void 0 ? _messages$errors$0$me : ''
    };
  } else if (messages.warnings && messages.warnings.length > 0) {
    var _messages$warnings$0$;
    return {
      type: 'warning',
      text: (_messages$warnings$0$ = messages.warnings[0].message) !== null && _messages$warnings$0$ !== void 0 ? _messages$warnings$0$ : ''
    };
  } else if (messages.info && messages.info.length > 0) {
    var _messages$info$0$mess;
    return {
      type: 'info',
      text: (_messages$info$0$mess = messages.info[0].message) !== null && _messages$info$0$mess !== void 0 ? _messages$info$0$mess : ''
    };
  } else if (messages.success && messages.success.length > 0) {
    var _messages$success$0$m;
    return {
      type: 'success',
      text: (_messages$success$0$m = messages.success[0].message) !== null && _messages$success$0$m !== void 0 ? _messages$success$0$m : ''
    };
  }
  return undefined;
}